import { useEffect, useRef, useState, Fragment } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import ComponentPreloader from '@/ui/preloaders/componentPreloader/ComponentPreloader';
import IndustryModal from '@/ui/modals/industryModal/IndustryModal';
import NewsCardSmall from '@/ui/card/newsCards/image/newsCardSmall/NewsCardSmall';
import PrimaryMenu from '@/ui/menus/primaryMenu/PrimaryMenu';
import Pagination from '@/ui/pagination/Pagination';
import SelectModalButton from '@/ui/buttons/selectModalButton/SelectModalButton';
import {
  CardWrapper,
  Container,
  SelectIndustryWrapper,
  MenuWrapper,
  NewsWrapper,
} from './IndustryNewsStyles';
import Nodata from '@/ui/errorSigns/noData/Nodata';
import gsap from 'gsap';
import { Transition, TransitionChild, Dialog, DialogPanel, DialogTitle } from '@headlessui/react';

const IndustryNews = ({
  industries,
  industryIdx,
  articles,
  totalN,
  page,
  handlePage,
  handleMenu,
  isLoading,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [t] = useTranslation('news');
  const [dT] = useTranslation('noData');
  const language = useSelector((state: any) => state.user.language);

  const [menuOffsetList, setMenuOffsetList] = useState<number | any>(0);

  useEffect(() => {
    if (openModal) {
      const body = document.querySelector('body');
      body.style.overflow = 'hidden';
    } else {
      const body = document.querySelector('body');
      body.style.overflow = null;
    }
  }, [openModal]);

  const ref = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuRef) return;
    const children = menuRef.current?.children;
    const list = [];
    let left = 0;
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      list.push({ offsetLeft: left, offsetWidth: child.offsetWidth });
      left += child.offsetWidth;
    }
    if (ref) {
      gsap.to(ref.current, {
        scrollLeft:
          list[industryIdx].offsetLeft -
          ref.current.offsetWidth / 2 +
          list[industryIdx].offsetWidth / 2,
      });
    }
    setMenuOffsetList(list);
  }, [menuRef, language]);

  const handleApply = (industryIdx) => {
    if (ref) {
      gsap.to(ref.current, {
        scrollLeft:
          menuOffsetList[industryIdx].offsetLeft -
          ref.current.offsetWidth / 2 +
          menuOffsetList[industryIdx].offsetWidth / 2,
      });
    }
    handleMenu(industryIdx);
  };

  return (
    <div className="space-y-12">
      <div className="space-y-3">
        <MenuWrapper ref={ref}>
          <PrimaryMenu
            ref={menuRef}
            sx={{ flexShrink: 0 }}
            itemMaxWidth={'250px'}
            menuList={industries.map((el) => el[`IndustryName_${language}`])}
            activeIndex={industryIdx}
            onClick={(index) => handleMenu(index)}
          />
        </MenuWrapper>

        <SelectIndustryWrapper>
          <SelectModalButton
            onClick={() => setOpenModal(!openModal)}
            title={t('Select Industry')}
          />
        </SelectIndustryWrapper>
      </div>

      {!isLoading ? (
        <>
          <NewsWrapper>
            {articles.map((article, i) => (
              <CardWrapper key={i}>
                <NewsCardSmall article={article} />
              </CardWrapper>
            ))}
          </NewsWrapper>
          {articles.length === 0 ? <Nodata desc={dT('No ESG news.')} /> : null}
        </>
      ) : (
        <ComponentPreloader width="1206px" height="300px" />
      )}
      <Transition appear show={openModal} as={Fragment}>
        <Dialog as="div" className="relative" onClose={setOpenModal}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" style={{ zIndex: 1000 }} />
          </TransitionChild>

          <div className="fixed inset-0 overflow-y-auto" style={{ zIndex: 1000 }}>
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel className="w-full max-w-[1200px] transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                  <DialogTitle
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 mb-4 hidden"
                  />

                  <IndustryModal
                    current={industryIdx}
                    apply={handleApply}
                    close={() => setOpenModal(!openModal)}
                  />
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>

      {!isLoading && (
        <Pagination
          totalPage={Math.ceil(totalN / 20)}
          currentPage={page}
          step={5}
          onClick={handlePage}
        />
      )}
    </div>
  );
};

export default IndustryNews;
